import { useQuery } from '@tanstack/react-query';
import { useApiClient } from 'utils/apiClient';
import {
  EmployeeReportTableParams,
  EmployeeRevenueDownloadParams,
} from 'utils/apiClient/services/reports/types';
import { downloadFile } from 'utils/api';
import { useIntl } from 'react-intl';
import { handleErrorNotification } from 'Common/utils';
import { Dayjs } from 'dayjs';

/* eslint-disable import/prefer-default-export */
export const useEmployeeReports = ({
  tableParams,
  enabled,
}: {
  tableParams: EmployeeReportTableParams;
  enabled: boolean;
}) => {
  const apiClient = useApiClient();
  const intl = useIntl();
  return useQuery({
    queryKey: ['reports', 'employee', tableParams],
    queryFn: async () => {
      try {
        const data = await apiClient.Reports.loadEmployeesRevenueReport(
          tableParams
        );
        return data;
      } catch (error: any) {
        if (error.response && error.response.status === 400) {
          handleErrorNotification({
            error,
            lang: intl.locale.toLowerCase(),
          });
        }
      }
      return null;
    },
    enabled,
    staleTime: 1500,
    refetchOnWindowFocus: false,
  });
};

export const useUserTransactions = ({
  userId,
  startDate,
  endDate,
  cursor,
  enabled,
  limit,
}: {
  userId: number;
  startDate: Dayjs;
  endDate: Dayjs;
  cursor: string | null;
  enabled: boolean;
  limit?: number;
}) => {
  const apiClient = useApiClient();
  const intl = useIntl();

  return useQuery({
    queryKey: [
      'reports',
      'user-transactions',
      userId,
      startDate,
      endDate,
      cursor,
      limit,
    ],
    queryFn: async () => {
      try {
        const data = await apiClient.Reports.loadUserTransactions({
          user_id: userId,
          start_date: startDate,
          end_date: endDate,
          cursor,
          limit,
        });
        return data;
      } catch (error: any) {
        if (error.response && error.response.status === 400) {
          handleErrorNotification({
            error,
            lang: intl.locale.toLowerCase(),
          });
        }
      }
      return null;
    },
    enabled,
    staleTime: 1500,
    refetchOnWindowFocus: false,
  });
};

export const useEmployeeExcelReport = ({
  start_date,
  end_date,
  report_name,
}: EmployeeRevenueDownloadParams) => {
  const apiClient = useApiClient();
  const { locale } = useIntl();
  return useQuery({
    queryKey: [
      'reports',
      'employee-excel-report',
      start_date,
      end_date,
      report_name,
    ],
    queryFn: async () => {
      try {
        const data = await apiClient.Reports.createEmployeeRevenueReport({
          start_date,
          end_date,
          report_name,
          locale: locale.toLowerCase(),
        });
        downloadFile(
          data,
          `${report_name}_${start_date.format('YYYYMMDD')}_${end_date.format(
            'YYYYMMDD'
          )}`,
          'xlsx'
        );
      } catch (error: any) {
        if (error.response && error.response.status === 400) {
          handleErrorNotification({
            error,
            lang: locale.toLowerCase(),
          });
        }
      }
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });
};
